import { useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { Box, IconButton, Typography } from '@mui/material'

const MAX_FILES = 1

// 🔍 detect video safely
const isVideo = file => {
  if (!file) return false
  if (typeof file === 'string') {
    return /\.(mp4|webm|ogg)$/i.test(file)
  }
  return file.type?.startsWith('video')
}

const FileUploaderMultiple = ({
  value = [],
  onChange = () => {},
  onRemove = () => {},
  accept = 'image/*,video/*',
}) => {
  const files = Array.isArray(value) ? value : []

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple: true,
    maxFiles: MAX_FILES,
    onDrop: acceptedFiles => {
      // 🚫 enforce max file limit
      const remainingSlots = MAX_FILES - files.length
      if (remainingSlots <= 0) return

      const filesToAdd = acceptedFiles.slice(0, remainingSlots)
      onChange([...files, ...filesToAdd])
    },
  })

  // 🧹 cleanup blob URLs
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file instanceof File) {
          URL.revokeObjectURL(file)
        }
      })
    }
  }, [files])

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {files.map((file, index) => {
        const preview =
          typeof file === 'string'
            ? file
            : URL.createObjectURL(file)

        return (
          <Box
            key={index}
            sx={{
              position: 'relative',
              width: 140,
              height: 140,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #ccc',
            }}
          >
            {isVideo(file) ? (
              <video
                src={preview}
                controls
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <img
                src={preview}
                alt="preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}

            <IconButton
              size="small"
              onClick={() => onRemove(index)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: '#fff',
                '&:hover': { backgroundColor: '#f0f0f0' },
              }}
            >
              ✕
            </IconButton>
          </Box>
        )
      })}

      {/* ➕ Add box (hidden when max reached) */}
      {files.length < MAX_FILES && (
        <Box
          {...getRootProps()}
          sx={{
            width: 140,
            height: 140,
            border: '1px dashed #aaa',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: '#fafafa',
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="caption" textAlign="center">
            Add Media
            <br />
            (Max {MAX_FILES})
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default FileUploaderMultiple
